import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertCircle, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatusBadge, type StatusTone } from '../../../components/design-system/admin';
import { ScoreChip, formatScore, scoreTone } from '../../../components/design-system/common/ScoreChip';
import type { NetworkBrandStatus, NetworkLinkEvidence, NetworkLinkMethod, NetworkLinkStatus, NetworkProductStatus } from '../../../common/models/network.model';
import type { VisualState } from '../../../common/hooks/network-admin.types';
import { OVERALL_THRESHOLDS, Thresholds, evidenceSignals, vocabKey } from './network-helpers';

/**
 * The small pieces every network page shares: statuses as a dot and a word,
 * the evidence chips, and the two notes (visual matching off, a failed call)
 * that must never be dressed up as success.
 */

const BRAND_TONE: Record<NetworkBrandStatus, StatusTone> = { partner: 'success', claimed: 'info', unclaimed: 'neutral' };
const PRODUCT_TONE: Record<NetworkProductStatus, StatusTone> = { published: 'success', draft: 'neutral' };
const LINK_TONE: Record<NetworkLinkStatus, StatusTone> = { confirmed: 'success', suggested: 'warning', rejected: 'danger' };

export function BrandStatusBadge({ status, className }: { status: NetworkBrandStatus; className?: string }) {
    const { t } = useTranslation();
    return (
        <StatusBadge tone={BRAND_TONE[status] ?? 'neutral'} className={className}>
            {t(`ADMIN_NETWORK.BRAND_STATUS.${vocabKey(status)}`, status)}
        </StatusBadge>
    );
}

export function ProductStatusBadge({ status, className }: { status: NetworkProductStatus; className?: string }) {
    const { t } = useTranslation();
    return (
        <StatusBadge tone={PRODUCT_TONE[status] ?? 'neutral'} className={className}>
            {t(`ADMIN_NETWORK.PRODUCT_STATUS.${vocabKey(status)}`, status)}
        </StatusBadge>
    );
}

export function LinkStatusBadge({ status, className }: { status: NetworkLinkStatus; className?: string }) {
    const { t } = useTranslation();
    return (
        <StatusBadge tone={LINK_TONE[status] ?? 'neutral'} className={className}>
            {t(`ADMIN_NETWORK.LINK_STATUS.${vocabKey(status)}`, status)}
        </StatusBadge>
    );
}

export const methodLabel = (t: TFunction, method: NetworkLinkMethod | string) => t(`ADMIN_NETWORK.METHOD.${vocabKey(method)}`, method);

/** A closed-vocabulary value (material, appearance, finish, colour family) in the active language. */
export const vocabLabel = (t: TFunction, value?: string | null) => (value ? t(`ADMIN_NETWORK.VOCAB.${vocabKey(value)}`, value) : '—');

/** The matcher's overall score: the large chip. */
export function OverallScoreChip({ score, className }: { score: number; className?: string }) {
    const { t } = useTranslation();
    return (
        <ScoreChip
            size="lg"
            label={t('ADMIN_NETWORK.SIGNAL.OVERALL')}
            value={formatScore(score)}
            tone={scoreTone(score, OVERALL_THRESHOLDS)}
            className={className}
            testId="overall-score"
        />
    );
}

/** EAN / ref / name / visual / format / brand / colour, each as a chip with its verdict. */
export function EvidenceChips({ evidence, thresholds, className }: { evidence: NetworkLinkEvidence; thresholds: Thresholds; className?: string }) {
    const { t } = useTranslation();
    return (
        <div className={cn('flex flex-wrap items-center gap-1.5', className)} data-testid="evidence-chips">
            {evidenceSignals(evidence, thresholds).map((s) => {
                const label = t(`ADMIN_NETWORK.SIGNAL.${vocabKey(s.key)}`);
                const value = s.score !== undefined ? formatScore(s.score) : t(`ADMIN_NETWORK.VERDICT.${vocabKey(s.state || 'unknown')}`);
                const title = s.detail ? `${label}: ${s.detail}` : s.key === 'visual' && evidence?.model ? `${label} · ${evidence.model}` : undefined;
                return <ScoreChip key={s.key} label={label} value={value} tone={s.tone} title={title} testId={`signal-${s.key}`} />;
            })}
        </div>
    );
}

/**
 * Said whenever SurfaceID was not used, with the reason the API gave. A run
 * without vision is a different (weaker) run, and reads as one.
 */
export function VisualOffNote({ visual, className }: { visual?: VisualState | null; className?: string }) {
    const { t } = useTranslation();
    if (!visual || visual.on) return null;
    return (
        <p className={cn('flex items-start gap-1.5 text-xs text-muted-foreground', className)} data-testid="visual-off">
            <EyeOff className="mt-px size-3.5 shrink-0 text-warning" aria-hidden />
            <span>{t('ADMIN_NETWORK.VISUAL_OFF', { reason: visual.reason || t('ADMIN_NETWORK.VISUAL_OFF_UNKNOWN') })}</span>
        </p>
    );
}

/** What failed, in the API's words, and a way to try again. */
export function ErrorNote({ message, onRetry, className }: { message: ReactNode; onRetry?: () => void; className?: string }) {
    const { t } = useTranslation();
    return (
        <div className={cn('flex flex-wrap items-center gap-2 text-sm text-destructive', className)} role="alert" data-testid="error-note">
            <AlertCircle className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">{message}</span>
            {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                    {t('ADMIN_SHELL.COMMON.RETRY')}
                </Button>
            )}
        </div>
    );
}

/** A label above a value, for attribute lists. */
export function FieldRow({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
    return (
        <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
            <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
            <dd className="min-w-0 text-sm break-words">{children}</dd>
        </div>
    );
}

export const FIELD_LABEL = 'text-xs font-medium text-muted-foreground';
